/**
 * Drain da outbox → mensagens do oplog (ver docs/features/SYNC.md).
 *
 * Os triggers de captura (capture.ts) gravam mudanças por coluna em `sync_outbox`, na ordem
 * das escritas (`seq` AUTOINCREMENT). O drain converte essas linhas em mensagens com HLC:
 *
 * - COLAPSA por célula (fica só a ÚLTIMA escrita de cada tabela/linha/coluna do lote) — o
 *   sync só precisa do estado final; a história intermediária local não interessa a ninguém.
 * - Carimba HLCs em ordem de `seq`, então a ordem causal local sobrevive à viagem (uma célula
 *   escrita depois carrega HLC maior).
 *
 * Puro: recebe linhas + relógio, devolve mensagens. Quem lê a outbox do SQLite e apaga as
 * linhas drenadas é a camada de cima (Fase 2 — transporte).
 */

import type { Hlc } from './hlc';
import { type Message, TOMBSTONE_COLUMN } from './messages';

/** Linha crua da `sync_outbox` (como sai do SELECT). */
export interface OutboxRow {
    seq: number;
    table_name: string;
    row_id: string;
    column_name: string;
    value: unknown;
}

/** Colapsa por célula (vence a maior `seq`) e carimba HLC em ordem de escrita. */
export function outboxRowsToMessages(rows: OutboxRow[], hlc: Hlc): Message[] {
    const latestPerCell = new Map<string, OutboxRow>();
    for (const row of [...rows].sort((a, b) => a.seq - b.seq)) {
        latestPerCell.set(`${row.table_name} ${row.row_id} ${row.column_name}`, row);
    }

    return [...latestPerCell.values()]
        .sort((a, b) => a.seq - b.seq)
        .map((row) => ({
            timestamp: hlc.send(),
            table: row.table_name,
            row: row.row_id,
            column: row.column_name,
            // Tombstone viaja como booleano; SQLite guarda 0/1.
            value: row.column_name === TOMBSTONE_COLUMN ? Boolean(row.value) : row.value,
        }));
}
